import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CLIENTS, type Client, type BrandKit } from "../data/clients";

export interface BrandOverride {
  logoUrl?: string;
  spokespersonUrl?: string;
  font?: string;
  primary?: string;
  secondary?: string;
  accent?: string;
  /** persona/cliente-ideal (texto livre, 1-3 frases) */
  persona?: string;
  /** público-alvo refinado (sobrescreve audience original) */
  audience?: string;
  /** tom de voz (1-2 frases) */
  voiceTone?: string;
  /** briefing geral livre */
  brief?: string;
  /** pitch (sobrescreve client.pitch) */
  pitch?: string;
  /** desafio principal (sobrescreve client.challenge) */
  challenge?: string;
  /** tags (csv) */
  tagsCsv?: string;
}

interface ClientBrandState {
  overrides: Record<string, BrandOverride>;
  set: (clientId: string, patch: BrandOverride) => void;
  clear: (clientId: string) => void;
  clearField: (clientId: string, field: keyof BrandOverride) => void;
}

export const useClientBrand = create<ClientBrandState>()(
  persist(
    (set) => ({
      overrides: {},
      set: (clientId, patch) => set(s => ({
        overrides: { ...s.overrides, [clientId]: { ...s.overrides[clientId], ...patch } }
      })),
      clear: (clientId) => set(s => {
        const next = { ...s.overrides }; delete next[clientId];
        return { overrides: next };
      }),
      clearField: (clientId, field) => set(s => {
        const cur = { ...(s.overrides[clientId] ?? {}) };
        delete cur[field];
        return { overrides: { ...s.overrides, [clientId]: cur } };
      })
    }),
    { name: "agencia-vpc-clientbrand-v1" }
  )
);

/** Brand efetivo: mistura definição estática + override do usuário. */
export interface EffectiveBrand extends BrandKit {
  spokespersonUrl?: string;
  logoUrl?: string;
  persona?: string;
  audience: string;
  voiceTone?: string;
  brief?: string;
  font?: string;
  pitch: string;
  challenge: string;
  tagsCsv: string;
}

export function effectiveBrand(client: Client, ov: BrandOverride | undefined): EffectiveBrand {
  return {
    primary:   ov?.primary   ?? client.brand.primary,
    secondary: ov?.secondary ?? client.brand.secondary,
    accent:    ov?.accent    ?? client.brand.accent,
    font:      ov?.font      ?? client.brand.font,
    logoUrl:   ov?.logoUrl   ?? client.brand.logoUrl,
    spokespersonUrl: ov?.spokespersonUrl,
    persona:   ov?.persona,
    audience:  ov?.audience  ?? client.audience,
    voiceTone: ov?.voiceTone,
    brief:     ov?.brief,
    pitch:     ov?.pitch     ?? client.pitch,
    challenge: ov?.challenge ?? client.challenge,
    tagsCsv:   ov?.tagsCsv   ?? client.tags.join(", ")
  };
}

/** Helper para o getter direto via ID */
export function getEffectiveBrand(clientId: string): EffectiveBrand | undefined {
  const c = CLIENTS.find(x => x.id === clientId);
  if (!c) return undefined;
  return effectiveBrand(c, useClientBrand.getState().overrides[clientId]);
}
